/**
 * Calculate total invoice amount including IVA and subtracting IRPF.
 * @param {number} base - The base amount before taxes.
 * @param {number} ivaPercent - The IVA percentage (e.g., 21, 10, 4, 0).
 * @param {number} irpfPercent - The IRPF percentage (e.g., 15, 7, 0).
 * @returns {Object} An object containing the base, ivaAmount, irpfAmount, and total.
 */
export const calculateTaxes = (base, ivaPercent = 21, irpfPercent = 0) => {
  const baseAmount = parseFloat(base) || 0;
  const ivaAmount = baseAmount * (parseFloat(ivaPercent) / 100);
  const irpfAmount = baseAmount * (parseFloat(irpfPercent) / 100);
  
  const total = baseAmount + ivaAmount - irpfAmount;

  return {
    base: baseAmount,
    ivaAmount,
    irpfAmount,
    total
  };
};

// Round to 2 decimals (euro cents), half-up.
export const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

// IBI (Impost sobre les Begudes Ensucrades Envasades) is a Catalan regional tax —
// it only applies to sales within Catalonia. Use the client's province to decide.
const CATALAN_PROVINCES = ['barcelona', 'girona', 'lleida', 'tarragona', 'gerona', 'lérida', 'lerida'];
export const isClientInCatalunya = (client) => {
  const prov = (client?.province || '').toLowerCase().trim();
  return prov.length > 0 && CATALAN_PROVINCES.some((cp) => prov.includes(cp));
};

// Recargo de equivalencia surcharge rate per IVA rate.
export const recargoRateFor = (r) => ({ 21: 5.2, 10: 1.4, 5: 0.62, 4: 0.5 }[Number(r)] || 0);

// Document totals computed the AEAT way: the cuota (IVA) and the recargo de
// equivalencia are worked out PER IVA rate and rounded to the cent on each rate,
// then those rounded figures are added up. This is how a Spanish factura is
// calculated by hand — so the printed breakdown rows always add up exactly to
// the totals. (Summing the raw per-line amounts and rounding only at the end can
// drift a cent, e.g. recargo 0,12 + 0,87 + 3,07 = 4,06, not 4,05.)
// `byRate` is the output of groupLinesByTaxRate.
export const sumByRateTotals = (byRate) => {
  let subtotal = 0, ivaTotal = 0, recargoTotal = 0, ibiTotal = 0;
  (byRate || []).forEach((r) => {
    // Subtotal = Base Imponible = pure product cost. IVA is charged on this alone;
    // the IBAE is surfaced separately and added on top (see docs/IBAE_TAX_FORMULA.md).
    subtotal += round2(r.productBase ?? r.base);
    ivaTotal += round2(r.iva);
    recargoTotal += round2(r.recargo);
    ibiTotal += round2(r.ibi);
  });
  return {
    subtotal: round2(subtotal),
    ivaTotal: round2(ivaTotal),
    recargoTotal: round2(recargoTotal),
    ibiTotal: round2(ibiTotal),
  };
};

// IBEE (Catalan tax on packaged sugary drinks) tariff per product tier, in €/L.
// 0 = not applicable, 1 = 0,08 €/L (5–8 g/100 ml), 2 = 0,12 €/L (>8 g/100 ml).
// NOTE: these are the original 2017 rates, kept BY CLIENT CHOICE. The current
// legal rates (since 1-Jul-2020, Llei 5/2020) are 0,10/0,15 — do NOT "correct"
// them without asking the client. Base = litres delivered (qty × L).
export const ibiRateFor = (tier) => ({ 1: 0.08, 2: 0.12 }[Number(tier)] || 0);

// IBI amount for a single document line: quantity × litres-per-unit × €/L tariff.
// It's a fixed per-litre tax, so the line discount (a price reduction) does not
// reduce it. Accepts either the editable-grid shape ({ volume_l, ibi }) or the
// stored DB row shape (same column names). IBI is a product-level tax (sugary
// drinks): it applies whenever the product carries an IBI tier + volume,
// independent of the client. The optional `applyIbi` flag, when explicitly set
// to false, lets a document opt out entirely; otherwise the line decides.
export const lineIbiAmount = (line, applyIbi = true) => {
  if (applyIbi === false) return 0;
  const qty = Number(line.quantity) || 0;
  const volume = Number(line.volume_l ?? line.volumeL) || 0;
  return qty * volume * ibiRateFor(line.ibi);
};

// Whether any line in a document carries IBI — used to decide if the IBI column
// should render in the printed tax breakdown, regardless of the client flag.
export const documentHasIbi = (lines) =>
  (lines || []).some((line) => lineIbiAmount(line) > 0);

// Prices are entered VAT-inclusive (the final price the customer pays) but stored
// as the net base (sin IVA), which is what the invoice/estimate totals build on.
// These convert between the two given an IVA rate.
export const grossToNet = (gross, rate) =>
  (parseFloat(gross) || 0) / (1 + (parseFloat(rate) || 0) / 100);
export const netToGross = (net, rate) =>
  (parseFloat(net) || 0) * (1 + (parseFloat(rate) || 0) / 100);

/**
 * Groups document lines by IVA rate for the printed tax breakdown table
 * (Tipo IVA / Base Imponible / Cuota / Recargo de equivalencia).
 * Accepts either the editable-grid line shape ({ unitPrice, taxRate }) or the
 * stored DB row shape ({ unit_price, tax_rate }).
 */
export const groupLinesByTaxRate = (lines, { recargoEquiv = false, applyIbi = true } = {}) => {
  const map = {};
  (lines || []).forEach((line) => {
    const qty = Number(line.quantity) || 0;
    const price = Number(line.unitPrice ?? line.unit_price) || 0;
    const discount = Number(line.discount) || 0;
    const rate = Number(line.taxRate ?? line.tax_rate) || 0;
    // Prefer the stored per-line net total (the amount actually billed) over
    // qty × unit_price. unit_price is persisted at 2 decimals, so on large
    // quantities re-deriving the base drifts cents from the issued figures — e.g.
    // 100 × 0,56 = 56,00 vs the billed 56,20 — which made the printed per-rate
    // rows disagree with the totals row. The stored `total` is already net, signed
    // and discount-applied; editable-grid lines have no `total`, so they fall back
    // to the live computation.
    const productBase = (line.total != null && line.total !== '')
      ? Number(line.total)
      : qty * price * (1 - discount / 100);
    // IBAE (Catalan sugary-drinks tax) applies ONLY when the document opted in
    // (applyIbi) — lineIbiAmount returns 0 otherwise. THE canonical rule (see
    // docs/IBAE_TAX_FORMULA.md — do NOT change): the IBAE is a fixed per-litre
    // charge added ON TOP of the invoice; it is NOT part of the IVA taxable base.
    // IVA and recargo are charged on the product base ALONE, and the IBAE is then
    // added once with no tax on it → Base Imponible excludes IBAE, and the tax
    // table columns (Base + Cuota + Recargo + IBAE) sum exactly to the total.
    const ibi = lineIbiAmount(line, applyIbi);
    const iva = productBase * rate / 100;
    const recargo = recargoEquiv ? productBase * recargoRateFor(rate) / 100 : 0;
    if (!map[rate]) map[rate] = { rate, base: 0, productBase: 0, iva: 0, recargo: 0, ibi: 0 };
    map[rate].base += productBase;   // Base Imponible = product base (IBAE excluded)
    map[rate].productBase += productBase;
    map[rate].iva += iva;
    map[rate].recargo += recargo;
    map[rate].ibi += ibi;
  });
  return Object.values(map).sort((a, b) => a.rate - b.rate);
};
